// The rights-holder state of the record (pipeline RC16; drawing c5-states.html, "A rights holder: MODUS Online Limited"
// and "InFront's streams after 1 July, the four readings"). A supplier's record shows the supply side where a client's
// shows use: what we pay them, potential from deals, the next date on their rights agreement, then who takes it and its
// supply, every word the one company read's own (RC13, U352). On InFront's own record, its clients' four readings, each
// client a link to its record; a client's record carries only a link to InFront's (brief 4b), never this list.
import { ActionButton } from '../parts/index.js';
import { useState } from 'react';
import type { CompanyRead, InFrontClientReading, InFrontClients } from './company-read';
import { gbp, plateDay } from './company-read';
import { needsSentence, type NeedsRow } from './AccountBar';

type RH = NonNullable<CompanyRead['rightsHolder']>;

export function RightsHolderBar({ rh, titled = false, needs = null }: { rh: RH; titled?: boolean; needs?: NeedsRow | null }) {
  const p = rh.potential;
  return (
    <div className="abar rh-bar" aria-label="The supply side">
      {titled ? <p className="rh-title">As a rights holder</p> : null}
      <div className="abar-cells">
        <div className="abar-cell">
          <span className="k">What we pay them</span>
          <span className="fig word">{rh.pay.words.label}</span>
          {rh.pay.words.note ? <span className="meta">{rh.pay.words.note}</span> : null}
        </div>
        <div className="abar-cell">
          <span className="k">Potential, from deals</span>
          {p.total !== null && p.total > 0
            ? <span className="fig">{gbp(p.total)}</span>
            : <span className="fig word">{p.deals === 0 && p.proposals.length === 0 ? 'No deal yet' : 'None on a deal'}</span>}
          {p.words.note ? <span className="meta">{p.words.note}</span> : null}
        </div>
        <div className="abar-cell">
          <span className="k">Next date</span>
          {rh.next
            ? <span className="abar-date"><span className="plate">{plateDay(rh.next.on)}</span><span className="meta">{rh.next.words}</span></span>
            : <span className="meta">{rh.agreements.length ? 'No notice point or end ahead on their rights agreement' : 'No rights agreement with them on the register.'}</span>}
        </div>
      </div>
      <ul className="abar-verdicts">
        <li><span className="vk">Who takes it</span><span className="level">{rh.takers.words.label}</span><span>{rh.takers.words.note}</span></li>
        <li><span className="vk">Supply</span><span className="level">{rh.supply.words.label}</span><span>{rh.supply.words.note ?? (rh.supply.state === 'not_linked' ? 'Read once a OnePlatform rights holder is confirmed as this company.' : '')}</span></li>
        {needs && needs.count ? <li><a href="#a-needs">Also needs you</a><span className="level">{needs.count}</span><span>{needsSentence(needs)}</span></li> : null}
      </ul>
    </div>
  );
}

const GROUPS: Array<{ key: InFrontClientReading; title: string }> = [
  { key: 'on_integration', title: 'On our integration' },
  { key: 'calls_falling', title: 'Calls falling' },
  { key: 'no_calls', title: 'No calls since July' },
  { key: 'went_direct', title: 'Went direct' },
];
const FIRST = 8;

/** InFront's record: each client that took its streams through iGame, under its reading, the biggest first. */
export function InFrontClientsSection({ clients, open }: { clients: InFrontClients; open: (path: string) => void }) {
  const [all, setAll] = useState<Set<InFrontClientReading>>(() => new Set());
  return (
    <section className="sec" id="a-infront-clients">
      <div className="sh"><h2>InFront's streams after 1 July</h2><span className="sl">{clients.clients.length === 1 ? 'one client' : `${clients.clients.length} clients`}</span></div>
      <div className="sb">
        <p className="lg-head">{clients.words.summary}</p>
        {GROUPS.map((g) => {
          const rows = clients.clients.filter((c) => c.reading === g.key);
          if (rows.length === 0) return null;
          const shown = all.has(g.key) ? rows : rows.slice(0, FIRST);
          return (
            <div key={g.key} className="ifc-group">
              <div className="hx-bandline"><h3>{g.title}</h3><span className="hx-cnt">{rows.length}</span></div>
              <ul className="ifc-list">
                {shown.map((c) => (
                  <li key={`${c.organisationId ?? c.name}`}>
                    {c.organisationId
                      ? <ActionButton reading type="button" className="go ifc-name" onClick={() => open(`/companies/${c.organisationId}`)}>{c.name}</ActionButton>
                      : <span className="ifc-name">{c.name}</span>}
                    <span className="level">{c.chip}</span>
                    <span className="ifc-said">{c.sentence}</span>
                  </li>
                ))}
              </ul>
              {rows.length > FIRST && !all.has(g.key)
                ? <ActionButton reading type="button" className="hx-link" onClick={() => setAll((was) => new Set([...was, g.key]))}>Show the other {rows.length - FIRST}</ActionButton>
                : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** On a client's InFront box: where the view across InFront's clients is, never the list itself (brief 4b). */
export function InFrontRecordLink({ read, open }: { read: CompanyRead; open: (path: string) => void }) {
  const r = read.inFrontRecord;
  if (!r) return null;
  return (
    <p className="if-link">
      How every client takes InFront's streams now is on <ActionButton reading type="button" className="go" onClick={() => open(`/companies/${r.id}`)}>{`${r.name}'s record`}</ActionButton>.
    </p>
  );
}
